import {
  Button,
  Checkbox,
  Icon,
  Modal,
  SearchBar,
  Select,
  type Column,
} from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { editorialCategories as editorialCategoriesApi } from "../../api/editorial-categories.service";
import { offices as officesApi } from "../../api/offices.service";
import { packages as packagesApi } from "../../api/packages.service";
import { subscriptions as subscriptionsApi } from "../../api/subscriptions.service";
import { DataFilters } from "../../components/DataFilters/DataFilters";
import { DataTable } from "../../components/DataTable/DataTable";
import { SortableHeader } from "../../components/DataTable/SortableHeader";
import EmptyState from "../../components/EmptyState";
import { FormSuccessScreen } from "../../components/Form/FormSuccessScreen";
import { SearchableSelect } from "../../components/Form/SearchableSelect";
import LoadingState from "../../components/LoadingState";
import { PaginationControls } from "../../components/PaginationControls";
import PermissionGate from "../../components/Permissions/PermissionGate";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import { useQuery } from "../../hooks/useApi";
import { useFilters } from "../../hooks/useFilters";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { useUrlParams } from "../../hooks/useUrlParams";
import type { Subscription } from "../../schemas/subscriptionSchema";
import { exportToCsv, type CSVColumn } from "../../utils";
import { CancelConfirmationModal } from "./CancelConfirmationModal";
import { COLUMN_CONFIGS, FILTER_CONFIGS, type SortField } from "./subscriptionConfig";
import styles from "./Subscriptions.module.css";
import { ROUTES } from "../../routes";
import type { ToastType } from "@legal-500/legal500-design-system";

type SubscriptionsPageContext = {
  refetchTrigger?: number;
  setToast?: (toast: { message: string; type: ToastType } | null) => void;
};

type SortDirection = "asc" | "desc";

import * as apiClient from "../../api/client";

const HIDDEN_COUNTRY_IDS = new Set(["1151955"]);

type RegionEntry = { id: string; name: string; icon: string | null };

function fetchSubscriptionRegions(): Promise<{ regions: RegionEntry[] }> {
  return apiClient.get<{ regions: RegionEntry[] }>("/v1/fulfilment/subscription-regions");
}

function SubscriptionsPage() {
  const navigate = useNavigate();
  const context = useOutletContext<SubscriptionsPageContext>();
  const { searchParams, updateUrl } = useUrlParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [siteStructureId, setSiteStructureId] = useState<string | null>(
    searchParams.get("siteStructureId") || null,
  );
  const [editorialCategoryId, setEditorialCategoryId] = useState<number | null>(
    searchParams.get("editorialCategoryId")
      ? Number(searchParams.get("editorialCategoryId"))
      : null,
  );
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "all",
    product: searchParams.get("product") || "all",
    expiringIn30Days: searchParams.get("expiringIn30Days") === "true",
    notFullyConfigured: searchParams.get("notFullyConfigured") === "true",
  });
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);
  const [sortField, setSortField] = useState<SortField | null>(
    (searchParams.get("sortField") as SortField) || null,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    (searchParams.get("sortDirection") as SortDirection) || "asc",
  );
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [cancelledCount, setCancelledCount] = useState(0);

  const {
    data: subscriptionsData,
    loading: subscriptionsLoading,
    error: subscriptionsError,
    refetch: refetchSubscriptions,
    webhookFallbackCount,
    webhookFallbackSearch,
  } = useSubscriptions({
    search,
    siteStructureId: siteStructureId ?? undefined,
    editorialCategoryId: editorialCategoryId ?? undefined,
    status: filters.status as "pending" | "complete" | "cancelled" | "all",
    productCode: filters.product,
    expiringInDays: filters.expiringIn30Days ? 30 : undefined,
    fullyConfigured: filters.notFullyConfigured ? false : undefined,
    sortField: sortField ?? undefined,
    sortDirection,
    page: currentPage,
    pageSize: itemsPerPage,
  });

  const {
    data: packagesData,
    loading: packagesLoading,
    error: packagesError,
  } = useQuery(packagesApi.getAll, []);

  const { data: countriesData } = useQuery(officesApi.getCountries, []);
  const { data: regionsData } = useQuery(fetchSubscriptionRegions, []);

  const { data: allCategories } = useQuery(editorialCategoriesApi.getAll, []);

  const regionEntries = useMemo(() => regionsData?.regions ?? [], [regionsData]);

  const countryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const region of regionEntries) {
      map.set(region.id, region.name);
    }
    if (countriesData) {
      for (const country of countriesData) {
        map.set(String(country.id), country.name);
      }
    }
    return map;
  }, [countriesData, regionEntries]);

  const subscriptions = subscriptionsData?.subscriptions ?? [];

  const countryOptions = useMemo(() => {
    const regionOpts = regionEntries.map(region => ({
      value: region.id,
      label: region.icon ? `${region.icon} ${region.name}` : region.name,
    }));
    const regionIds = new Set(regionEntries.map(r => r.id));
    const countryOpts = (countriesData ?? [])
      .filter(
        country =>
          !regionIds.has(String(country.id)) && !HIDDEN_COUNTRY_IDS.has(String(country.id)),
      )
      .map(country => ({
        value: String(country.id),
        label: country.name,
      }));
    const allOptions = [...regionOpts, ...countryOpts];
    allOptions.sort((a, b) => a.label.localeCompare(b.label));
    return allOptions;
  }, [countriesData]);

  const practiceAreaOptions = useMemo(() => {
    if (!allCategories) return [];
    return allCategories
      .filter(cat => cat.type === "practice_area")
      .map(cat => ({
        value: Number(cat.id),
        label: cat.name,
      }));
  }, [allCategories]);

  const productFilterOptions = useMemo(() => {
    if (!packagesData?.packages) {
      return [{ value: "all", label: "All Products" }];
    }

    const uniqueProductCodes = Array.from(
      new Set(packagesData.packages.map(pkg => pkg.productCode)),
    ).sort();

    return [
      { value: "all", label: "All Products" },
      ...uniqueProductCodes.map(code => ({
        value: code,
        label: code,
      })),
    ];
  }, [packagesData]);

  const dynamicFilterConfigs = useMemo(() => {
    return [...FILTER_CONFIGS, { key: "product", label: "Product", options: productFilterOptions }];
  }, [productFilterOptions]);

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters,
    updateUrl,
    labelOverrides: {
      expiringIn30Days: "Expiring in 30 days",
      notFullyConfigured: "Not fully configured",
    },
  });

  const handleSort = (field: SortField) => {
    let newDirection: SortDirection = "asc";
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortDirection(newDirection);
    updateUrl({ sortField: field, sortDirection: newDirection });
  };

  const filteredSubscriptions = subscriptions;

  const hasActiveSearchOrFilters =
    search ||
    siteStructureId ||
    editorialCategoryId ||
    filters.status !== "all" ||
    filters.product !== "all" ||
    filters.expiringIn30Days ||
    filters.notFullyConfigured;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    siteStructureId,
    editorialCategoryId,
    filters.status,
    filters.product,
    filters.expiringIn30Days,
    filters.notFullyConfigured,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (context.refetchTrigger !== undefined && context.refetchTrigger > 0) {
      refetchSubscriptions();
    }
  }, [context.refetchTrigger]);

  const serverTotal = subscriptionsData?.pagination?.total ?? 0;
  const totalPages = Math.ceil(serverTotal / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, serverTotal);

  const paginatedSubscriptions = filteredSubscriptions;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedSubscriptions.map(subscription => subscription.id));
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleRowSelect = (subscriptionId: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(subscriptionId);
    } else {
      newSelected.delete(subscriptionId);
    }
    setSelectedRows(newSelected);
  };

  const subscriptionCsvColumns: CSVColumn<Subscription>[] = [
    { header: "Company", getValue: subscription => subscription.companyName || "" },
    { header: "Product", getValue: subscription => subscription.package?.productCode || "" },
    {
      header: "Country / Region",
      getValue: subscription =>
        subscription.siteStructureId
          ? (countryNameById.get(String(subscription.siteStructureId)) ?? "")
          : "",
    },
    { header: "Site", getValue: subscription => subscription.site },
    { header: "Language", getValue: subscription => subscription.language },
    { header: "Start Date", getValue: subscription => subscription.startDate },
    { header: "End Date", getValue: subscription => subscription.endDate },
    { header: "Status", getValue: subscription => subscription.status },
  ];

  const handleExportCSV = () => {
    const subscriptionsToExport =
      selectedRows.size > 0
        ? filteredSubscriptions.filter(subscription => selectedRows.has(subscription.id))
        : filteredSubscriptions;

    exportToCsv(subscriptionsToExport, subscriptionCsvColumns, {
      filename: "subscriptions",
    });
  };

  const handleCancelSubscriptions = async () => {
    if (selectedRows.size === 0) return;

    try {
      await Promise.all(
        Array.from(selectedRows).map(subscriptionId => subscriptionsApi.cancel(subscriptionId)),
      );

      await refetchSubscriptions();

      setCancelledCount(selectedRows.size);
      setSelectedRows(new Set());
      setShowCancelModal(false);
      setShowCancelSuccess(true);
    } catch (err) {
      console.error("Failed to cancel subscriptions:", err);
      throw err;
    }
  };

  const checkboxColumn: Column<Subscription> = {
    header: (
      <Checkbox
        checked={
          selectedRows.size === paginatedSubscriptions.length && paginatedSubscriptions.length > 0
        }
        onChange={e => handleSelectAll(e.target.checked)}
      />
    ) as unknown as string,
    accessor: "id",
    render: (_, row) => (
      <div style={{ padding: "0.5rem" }} onClick={e => e.stopPropagation()}>
        <Checkbox
          checked={selectedRows.has(row.id)}
          onChange={e => handleRowSelect(row.id, e.target.checked)}
        />
      </div>
    ),
  };

  const companyNameColumn: Column<Subscription> = {
    header: "Company",
    accessor: "companyName",
    render: (_, row) => (
      <Link
        to={ROUTES.fulfilment.subscriptionDetail(row.id)}
        onClick={e => e.stopPropagation()}
        style={{ color: "inherit", textDecoration: "none" }}
      >
        {row.companyName || ""}
      </Link>
    ),
  };

  const companyIdColumn: Column<Subscription> = {
    header: "Company ID",
    accessor: "firmId",
    render: (_, row) =>
      row.firmId ? (
        <Link
          to={ROUTES.fulfilment.firmDashboard(row.firmId)}
          onClick={e => e.stopPropagation()}
          style={{ color: "var(--color-primary)", textDecoration: "none", fontWeight: 500 }}
        >
          {row.firmId}
        </Link>
      ) : (
        ""
      ),
  };

  const columns: Column<Subscription>[] = [
    checkboxColumn,
    companyNameColumn,
    companyIdColumn,
    ...COLUMN_CONFIGS.filter(config => config.accessor !== "companyName").map(config => {
      const renderFn =
        config.accessor === "siteStructureId"
          ? (value: unknown) => {
              const countryName = value ? countryNameById.get(String(value)) : null;
              return countryName || "";
            }
          : config.render
            ? (value: unknown, row: Subscription) =>
                config.render!(value as Subscription[typeof config.accessor], row)
            : undefined;

      return {
        header: config.sortable
          ? ((
              <SortableHeader
                label={config.header}
                field={config.sortField! as SortField}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            ) as unknown as string)
          : config.header,
        accessor: config.accessor,
        render: renderFn,
      };
    }),
  ];

  const isInitialLoading = (subscriptionsLoading || packagesLoading) && subscriptions.length === 0;
  const isRefetching = subscriptionsLoading && subscriptions.length > 0;
  const error = subscriptionsError || packagesError;

  if (isInitialLoading) {
    return <LoadingState message="Loading subscriptions..." />;
  }

  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }

    return (
      <EmptyState
        variant="error"
        title="Failed to load subscriptions"
        message={error.message}
        action={{
          label: "Retry",
          onClick: () => refetchSubscriptions(),
        }}
      />
    );
  }

  return (
    <>
      <div className={styles.searchRow}>
        <div className={styles.searchField}>
          <label className={styles.fieldLabel}>Search</label>
          <div className={styles.searchSection}>
            <SearchBar
              value={search}
              onChange={value => {
                setSearch(value);
                updateUrl({ search: value });
              }}
              onClear={() => {
                setSearch("");
                updateUrl({ search: "" });
              }}
              placeholder="Search by firm name, ID, slug, CRM ID, order ID, correlation ID..."
            />
          </div>
        </div>
        <div className={styles.searchField}>
          <SearchableSelect
            name="regionFilter"
            label="Country / Region"
            value={siteStructureId}
            options={countryOptions}
            onChange={val => {
              setSiteStructureId(val !== null ? String(val) : null);
              updateUrl({ siteStructureId: val !== null ? String(val) : "" });
            }}
            placeholder="All"
          />
        </div>
        <div className={styles.searchField}>
          <SearchableSelect
            name="practiceAreaFilter"
            label="Practice Area"
            value={editorialCategoryId}
            options={practiceAreaOptions}
            onChange={val => {
              const numVal = val !== null ? Number(val) : null;
              setEditorialCategoryId(numVal);
              updateUrl({ editorialCategoryId: numVal ? String(numVal) : "" });
            }}
            placeholder="All"
          />
        </div>
      </div>

      <div className={styles.filtersRow}>
        {dynamicFilterConfigs.map(filter => (
          <div key={filter.key} className={styles.filterDropdown}>
            <Select
              label={filter.label}
              value={filters[filter.key as keyof typeof filters] as string}
              onChange={e => handleFilterChange(filter.key, e.target.value)}
              options={filter.options}
              size="small"
            />
          </div>
        ))}
        <div className={styles.expiringCheckbox}>
          <Checkbox
            checked={filters.expiringIn30Days}
            onChange={e => handleFilterChange("expiringIn30Days", e.target.checked)}
            label="Expiring in 30 days"
          />
        </div>
        <div className={styles.expiringCheckbox}>
          <Checkbox
            checked={filters.notFullyConfigured}
            onChange={e => handleFilterChange("notFullyConfigured", e.target.checked)}
            label="Not fully configured"
          />
        </div>
      </div>

      <DataFilters
        filters={[]}
        values={{ status: filters.status, product: filters.product }}
        onChange={handleFilterChange}
        activeFilters={activeFilters}
        onClearFilter={handleClearFilter}
      />

      <div className={styles.actionsRow}>
        {selectedRows.size > 0 ? (
          <>
            <Button onClick={handleExportCSV} size="small" className={styles.exportButton}>
              Export CSV ({selectedRows.size})
            </Button>
            <PermissionGate permission="fulfilment.edit_subscription">
              <Button onClick={() => setShowCancelModal(true)} size="small">
                Cancel {selectedRows.size} subscription{selectedRows.size > 1 ? "s" : ""}
              </Button>
            </PermissionGate>
          </>
        ) : (
          <Button onClick={handleExportCSV} size="small" className={styles.exportButton}>
            Export all as CSV
          </Button>
        )}
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="itemsPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <Select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={e => {
              const newPageSize = Number(e.target.value);
              setItemsPerPage(newPageSize);
              setCurrentPage(1);
              updateUrl({ pageSize: String(newPageSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        {isRefetching && (
          <div className={styles.tableLoadingOverlay}>
            <div className={styles.loadingSpinner} />
          </div>
        )}
        <DataTable
          data={paginatedSubscriptions}
          columns={columns}
          onRowClick={subscription =>
            navigate(ROUTES.fulfilment.subscriptionDetail(subscription.id))
          }
          emptyMessage={
            hasActiveSearchOrFilters
              ? webhookFallbackCount
                ? `No subscriptions found, however ${webhookFallbackCount} CRM notification${webhookFallbackCount === 1 ? "" : "s"} match your search.`
                : "No subscriptions found matching your search or filters"
              : "No subscriptions found. Click 'Create' to add one."
          }
        />
        {webhookFallbackCount && webhookFallbackSearch && subscriptions.length === 0 && (
          <div style={{ padding: "1rem", textAlign: "center" }}>
            <Link
              to={`/fulfilment/crm-webhook-logs?search=${encodeURIComponent(webhookFallbackSearch)}&page=1`}
            >
              View {webhookFallbackCount} matching CRM notification
              {webhookFallbackCount === 1 ? "" : "s"}
            </Link>
          </div>
        )}
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        startIndex={startIndex}
        endIndex={endIndex}
        totalCount={serverTotal}
        itemName="subscriptions"
        onPageChange={page => {
          setCurrentPage(page);
          updateUrl({ page: String(page) });
        }}
      />

      {showCancelModal && (
        <CancelConfirmationModal
          subscriptions={filteredSubscriptions.filter(subscription =>
            selectedRows.has(subscription.id),
          )}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelSubscriptions}
        />
      )}

      {showCancelSuccess && (
        <Modal
          isOpen={showCancelSuccess}
          onClose={() => setShowCancelSuccess(false)}
          title=""
          size="large"
          blurBackground
        >
          <FormSuccessScreen
            mode="cancel"
            itemName={`${cancelledCount} subscription${cancelledCount > 1 ? "s" : ""}`}
            singularName="Subscription"
            onClose={() => {
              setShowCancelSuccess(false);
            }}
          />
        </Modal>
      )}
    </>
  );
}

export default SubscriptionsPage;
